import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, CheckCircle, Printer, X, Wallet, ArrowLeft } from 'lucide-react';
import SummaryApi from '../../common';
import fetchWithAuth from '../../common/fetchWithAuth';
import { toast } from 'react-toastify';

const ALL_MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear];

export default function SalaryManagement() {
    const navigate = useNavigate();
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [staffList, setStaffList] = useState([]);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal State
    const [showPayModal, setShowPayModal] = useState(false);
    const [payData, setPayData] = useState({
        staffId: '',
        staffName: '',
        baseSalary: 0,
        unpaidLeaves: 0,
        netSalary: 0, // This is the final amount to pay
        paymentMode: 'Cash',
        bankId: '',
        referenceNo: ''
    });

    const fetchStaff = async () => {
        try {
            setLoading(true);
            const res = await fetchWithAuth(SummaryApi.getAllStaff.url, {
                method: SummaryApi.getAllStaff.method
            });
            const data = await res.json();
            if (data.data) {
                setStaffList(data.data);
            }
        } catch (error) {
            console.error("Error fetching staff:", error);
            toast.error("Failed to load staff list");
        } finally {
            setLoading(false);
        }
    };

    const fetchBanks = async () => {
        try {
            const res = await fetchWithAuth(SummaryApi.getAllBanks.url, {
                method: SummaryApi.getAllBanks.method
            });
            const data = await res.json();
            if (data.success) {
                setBanks(data.data || []);
            }
        } catch (error) {
            console.error("Error fetching banks:", error);
        }
    };

    useEffect(() => {
        fetchStaff();
        fetchBanks();
    }, []);

    const monthName = ALL_MONTHS[selectedMonth];
    const monthKey = `${monthName} ${selectedYear}`;

    const getSalaryData = (staff) => {
        const record = staff.salaryStatus?.find(s => s.month === monthKey);
        if (record) {
            return {
                ...record,
                baseSalary: record.salaryAmount,
            };
        }
        return {
            baseSalary: staff.salary || 0,
            unpaidLeaves: 0,
            netSalary: staff.salary || 0,
            status: "Unpaid",
            _isNew: true
        };
    };

    const openPayModal = (staff, currentData) => {
        setPayData({
            staffId: staff._id,
            staffName: staff.name,
            baseSalary: currentData.baseSalary,
            unpaidLeaves: currentData.unpaidLeaves || 0,
            netSalary: currentData.baseSalary, // Default to base salary, no auto-calc
            paymentMode: 'Cash',
            bankId: '',
            referenceNo: ''
        });
        setShowPayModal(true);
    };

    const handlePayChange = (e) => {
        const { name, value } = e.target;
        setPayData(prev => ({ ...prev, [name]: value }));
    };

    const handleProcessPayment = async (e) => {
        e.preventDefault();

        // Validation: Check Bank Balance
        if (payData.bankId) {
            const selectedBank = banks.find(b => b._id === payData.bankId);
            if (selectedBank && selectedBank.balance < Number(payData.netSalary)) {
                toast.error(`Insufficient Bank Balance! Available: ₹${selectedBank.balance}`);
                return;
            }
        }

        try {
            const payload = {
                id: payData.staffId,
                month: monthKey,
                unpaidLeaves: Number(payData.unpaidLeaves),
                netSalary: Number(payData.netSalary),
                status: 'Paid',
                paymentMode: payData.paymentMode,
                bankId: payData.bankId || null,
                referenceNo: payData.referenceNo
            };

            const res = await fetchWithAuth(SummaryApi.updateSalaryStatus.url, {
                method: SummaryApi.updateSalaryStatus.method,
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.success) {
                toast.success("Payment successful & Recorded!");
                setShowPayModal(false);
                fetchStaff(); // Refresh list to show 'Paid' status
            } else {
                toast.error(data.message || "Payment failed");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error processing payment");
        }
    };

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <div className="mb-4">
                <button
                    onClick={() => navigate('/dashboard/employees')}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors mb-4"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Employees</span>
                </button>
            </div>
            <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <span className="p-2 bg-emerald-100 rounded-xl text-emerald-600">
                            <User size={32} />
                        </span>
                        Salary Management
                    </h1>
                </div>

                {/* Month/Year Selector */}
                <div className="flex items-center bg-white rounded-xl shadow-sm border border-gray-200 p-1">
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                        className="px-4 py-2 bg-transparent font-medium text-gray-700 outline-none cursor-pointer hover:bg-gray-50 rounded-lg transition-colors border-r border-gray-100"
                    >
                        {ALL_MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                    </select>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="px-4 py-2 bg-transparent font-medium text-gray-700 outline-none cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                    >
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">S.No.</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Base Salary</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Unpaid Leaves</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Net Payable</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {loading ? (
                            <tr><td colSpan="6" className="text-center py-8">Loading...</td></tr>
                        ) : staffList.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-8 text-gray-400">No staff found</td></tr>
                        ) : (
                            staffList.map((staff, index) => {
                                const data = getSalaryData(staff);
                                const isPaid = data.status === 'Paid';

                                return (
                                    <tr key={staff._id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-bold">
                                                    {staff.name.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="font-medium text-gray-900">{staff.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">₹{Number(data.baseSalary).toLocaleString()}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{data.unpaidLeaves}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-gray-800">
                                            ₹{Number(data.netSalary).toLocaleString()}
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex justify-end gap-2">
                                                {isPaid ? (
                                                    <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-bold flex items-center gap-1">
                                                        <CheckCircle size={12} />
                                                        Paid
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => openPayModal(staff, data)}
                                                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                                                    >
                                                        <Wallet size={14} />
                                                        Pay
                                                    </button>
                                                )}

                                                {isPaid && <button className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-xs font-medium transition-colors flex items-center gap-1">
                                                    <Printer size={12} />
                                                    Slip
                                                </button>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pay Modal */}
            {showPayModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-xl animate-in fade-in zoom-in-95">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800">Process Salary Payment</h3>
                            <button onClick={() => setShowPayModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleProcessPayment} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
                                <input
                                    value={payData.staffName}
                                    readOnly
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unpaid Leaves</label>
                                    <input
                                        type="number"
                                        name="unpaidLeaves"
                                        min="0"
                                        value={payData.unpaidLeaves}
                                        onChange={handlePayChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:border-emerald-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                                    <input
                                        value={payData.baseSalary}
                                        readOnly
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Net Payable Amount</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                                    <input
                                        type="number"
                                        name="netSalary"
                                        value={payData.netSalary}
                                        onChange={handlePayChange}
                                        className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-xl focus:border-emerald-500 font-bold text-gray-800 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                                    <select
                                        name="paymentMode"
                                        value={payData.paymentMode}
                                        onChange={handlePayChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:border-emerald-500 outline-none"
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI</option>
                                        <option value="Check">Check</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Bank</label>
                                    <select
                                        name="bankId"
                                        value={payData.bankId}
                                        onChange={handlePayChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:border-emerald-500 outline-none"
                                    >
                                        <option value="">Select Bank Account</option>
                                        {banks.map(bank => (
                                            <option key={bank._id} value={bank._id}>
                                                {bank.accountName || bank.bankName} - ₹{bank.balance}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {(payData.paymentMode === 'UPI' || payData.paymentMode === 'Check' || payData.paymentMode === 'Bank Transfer') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference No.</label>
                                    <input
                                        name="referenceNo"
                                        placeholder="Enter Transaction ID / Check No"
                                        value={payData.referenceNo}
                                        onChange={handlePayChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:border-emerald-500 outline-none"
                                    />
                                </div>
                            )}

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowPayModal(false)}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 shadow-lg shadow-emerald-200"
                                >
                                    Confirm Payment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
